import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useAuth } from "./AuthContext";
import "./Payment.css";

// Load Stripe outside of component to avoid recreating it on each render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Helper function to get user's full name from different possible property names
const getUserFullName = (user) => {
  if (!user) return "Diviti Adora Customer";

  // Try different possible property names for first name
  const firstName =
    user.firstName ||
    user.first_name ||
    user.given_name ||
    user.givenName ||
    "";

  // Try different possible property names for last name
  const lastName =
    user.lastName ||
    user.last_name ||
    user.family_name ||
    user.familyName ||
    "";

  // If we have at least one name part, return it
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  // If no name parts found, try a full name property
  if (user.name || user.fullName || user.full_name) {
    return user.name || user.fullName || user.full_name;
  }

  // If no name found at all, return the default
  return "Diviti Adora Customer";
};

const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [customerSecret, setCustomerSecret] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const navigate = useNavigate();

  // Debug user object and initialize customer name
  useEffect(() => {
    console.log("User object in Payment component:", user);
    if (user) {
      const fullName = getUserFullName(user);
      if (fullName !== "Diviti Adora Customer") {
        setCustomerName(fullName);
      } else {
        // If we couldn't determine the user's name, prompt them to enter it
        setIsEditingName(true);
      }
    }
  }, [user]);

  // Retrieve token from local storage
  const token = localStorage.getItem("jwtToken");

  useEffect(() => {
    // Create a setup intent as soon as the component loads
    const createSetupIntent = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/payments/create-setup-intent`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClientSecret(response.data.clientSecret);
        setCustomerSecret(response.data.customer_session_client_secret);
      } catch (err) {
        console.error("Error creating setup intent:", err);
        setError("Failed to initialize payment setup. Please try again later.");
      }
    };
    createSetupIntent();
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
      setProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    // Use your card Element with other Stripe.js APIs
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: customerName || getUserFullName(user),
        },
      },
    });

    if (error) {
      setError(`Payment failed: ${error.message}`);
      setProcessing(false);
      return;
    }

    // The setup has succeeded. Send the payment method ID to your server to save it to the customer
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/payments/save-payment-method`,
        { paymentMethodId: setupIntent.payment_method },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSucceeded(true);
        setError(null);
        // Redirect to member dashboard after a short delay
        setTimeout(() => {
          navigate("/member");
        }, 2000);
      } else {
        setError("Failed to save payment method. Please try again.");
      }
    } catch (err) {
      console.error("Error saving payment method:", err);
      setError("Failed to save payment method. Please try again.");
    }

    setProcessing(false);
  };

  const cardElementOptions = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <h2>Add Payment Method</h2>
      <p className="payment-info">
        Your payment information is required to book sessions. Your card will
        not be charged unless you have a membership or purchase a package or
        drop-in session.
      </p>
      <div className="form-group">
        <label htmlFor="card-element">Credit or Debit Card</label>
        <div className="customer-name-display">
          {isEditingName ? (
            <div className="name-edit-container">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter your name"
                className="name-edit-input"
              />
              <div className="name-edit-buttons">
                <button
                  type="button"
                  className="name-edit-button save"
                  onClick={() => setIsEditingName(false)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="name-edit-button cancel"
                  onClick={() => {
                    setCustomerName(getUserFullName(user));
                    setIsEditingName(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="name-display-container">
              <p>
                This card will be saved for:{" "}
                <strong>
                  {customerName ||
                    (user ? getUserFullName(user) : "Diviti Adora Customer")}
                </strong>
              </p>
              <button
                type="button"
                className="edit-name-button"
                onClick={() => setIsEditingName(true)}
              >
                Edit
              </button>
            </div>
          )}
        </div>
        <div className="card-element-container">
          <CardElement id="card-element" options={cardElementOptions} />
        </div>
      </div>
      <button
        disabled={processing || !stripe || succeeded}
        className="payment-button"
      >
        {processing ? "Processing..." : "Save Payment Method"}
      </button>
      {error && <div className="error-message">{error}</div>}
      {succeeded && (
        <div className="success-message">
          Payment method added successfully! Redirecting to your dashboard...
        </div>
      )}
    </form>
  );
};

function Payment() {
  return (
    <div className="payment-container">
      <Elements stripe={stripePromise}>
        <PaymentForm />
      </Elements>
    </div>
  );
}

export default Payment;
